/**
 * `chat` namespace — Korean translations.
 *
 * Mirrors `en/chat.ts` exactly; the `Record<ChatKey, string>` annotation makes a
 * missing or misspelled key a compile error. Strategy→전략, Refine→개선, Dry-run→모의
 * 실행. Ticker symbols, RSI, DSL, JSON, AI, OpenAI/Anthropic stay English. Korean has
 * no plural inflection, so the singular/plural pairs resolve to one form each.
 */
import type { ChatKey } from "../en/chat";

export const chat: Record<ChatKey, string> = {
  eyebrow: "AI 빌더",
  pageTitle: "전략 챗",
  pageDescription: "대화하며 전략을 작성하고 다듬습니다. DSL 문서가 그 옆에서 함께 만들어집니다.",
  sessionsHeading: "세션",
  sessionsLoadError: "세션을 불러오지 못했습니다.",

  startSessionHeading: "세션 시작",
  startSessionDesc:
    "전략을 처음부터 새로 만들거나, 기존 전략의 실시간 문서와 성과 기록을 컨텍스트에 담아 다듬습니다.",
  titleLabel: "제목 (선택)",
  titlePlaceholder: "예: RSI 저점 매수",
  baseLabel: "기반 전략",
  startFresh: "새로 시작",
  refineOption: "개선 · {name}",
  starting: "시작 중…",
  startBuilding: "빌드 시작",
  startError: "세션을 시작하지 못했습니다.",

  aiNotConfigured: "AI가 설정되지 않았습니다",
  aiLockedDesc:
    "전략 빌더에는 OpenAI 또는 Anthropic 키가 필요합니다. 설정 → AI에서 키를 추가하면 대화형 전략 빌드를 사용할 수 있습니다. 아래에서 지난 세션은 계속 열람할 수 있습니다.",
  configureAi: "AI 설정",

  untitledSession: "제목 없는 세션",
  refining: "개선 중",
  hasDraft: "초안 있음",
  updated: "업데이트 {date}",
  sessionsEmptyTitle: "아직 세션이 없습니다",
  sessionsEmptyDesc: "위에서 세션을 시작해 첫 전략을 챗으로 만들어 보세요.",
  deleteSession: "삭제",
  deleteConfirm: "정말 삭제할까요?",
  deleting: "삭제 중…",
  deleteError: "세션을 삭제하지 못했습니다. 다시 시도해 주세요.",

  loadingSession: "세션 불러오는 중…",
  sessionLoadError: "이 세션을 불러올 수 없습니다.",
  backToSessions: "세션 목록으로",
  builderTitle: "전략 빌더",

  reasoning: "추론",
  reasoningStreaming: "추론 중…",
  toolWriting: "문서 작성 중…",
  toolWrote: "문서 재작성함",
  toolEditing: "문서 편집 중…",
  toolEdited: "문서 편집함",
  changeOne: "변경 {count}건",
  changeMany: "변경 {count}건",
  toolReadStats: "성과 기록 읽음",
  toolReadBacktest: "백테스트 결과 읽음",
  toolSearching: "웹 검색 중…",
  toolSearched: "웹 검색함",
  toolFetching: "페이지 읽는 중…",
  toolFetched: "페이지 읽음",
  toolFindingTools: "도구 찾는 중…",
  toolFoundTools: "도구 검색함",
  statsTradesOne: "거래 {trades}건",
  statsTradesMany: "거래 {trades}건",
  statsWinRateSuffix: ", 승률 {pct}%",

  // ── conversation: expanded tool detail ──
  editSet: "설정",
  editValid: "유효함",
  editMissing: "누락 {count}",
  editErrors: "오류 {count}",
  searchNoResults: "결과 없음",
  searchFailed: "검색에 실패했습니다",
  toolSearchNoMatch: "일치하는 도구 없음",
  toolSearchLoaded: "불러옴: {names}",
  fetchEmpty: "읽을 수 있는 내용 없음",
  fetchFailed: "페이지를 읽지 못했습니다",
  fetchTruncated: "잘림",
  detailInput: "입력",
  detailOutput: "출력",
  toolFailed: "실행 실패",

  thinking: "생각 중…",
  resumingTurn: "이어서 진행 중…",
  messageRenderError: "이 메시지를 표시할 수 없습니다.",
  introTitle: "원하는 전략을 설명해 주세요",
  introExample:
    "예: “KODEX 200의 14일 RSI가 30 아래로 떨어지면 예산의 20% 규모로 매수하고 알림 — 모의 실행만.” 문서가 형태를 갖춰 가며 오른쪽에 나타납니다.",
  errorLocked:
    "AI가 설정되지 않았습니다 — 설정 → AI에서 제공자 키를 추가하면 전략을 빌드할 수 있습니다.",
  errorRefusal:
    "모델이 이 요청을 거절했습니다. 문서는 그대로 유지됐습니다. 표현을 바꿔 다시 시도해 주세요.",
  errorTurnFailed: "요청에 실패했습니다. 다시 시도해 주세요.",

  composerPlaceholder: "전략 빌더에게 메시지…",
  stop: "중지",
  send: "전송",

  docTitle: "전략 문서",
  noDocument: "아직 문서 없음",
  untitledStrategy: "제목 없는 전략",
  badgeEmpty: "비어 있음",
  badgeValid: "유효한 DSL",
  issueOne: "문제 {count}개",
  issueMany: "문제 {count}개",
  modeStructured: "구조화",
  modeJson: "JSON",
  syncFailed: "동기화 실패",
  savingDraft: "초안 저장 중…",
  draftSynced: "초안 동기화됨",

  issuesTitle: "이 문서는 아직 유효한 전략이 아닙니다",
  issuesMore: "+{count}개 더…",
  errorNotObject: "문서는 JSON 객체여야 합니다.",
  errorInvalidJson: "잘못된 JSON",

  emptyDocTitle: "문서가 여기서 스스로 만들어집니다",
  emptyDocDesc:
    "왼쪽에서 전략을 설명해 주세요. 어시스턴트가 초안을 작성하면 DSL이 스트리밍되고 모든 필드를 편집할 수 있게 됩니다.",

  saveNamePlaceholder: "전략 이름 (선택)",
  saving: "저장 중…",
  saveAsStrategy: "전략으로 저장",
  saveHintCount:
    "저장하기 전에 검증 문제 {count}개를 해결해 주세요 — 새 전략은 항상 모의 실행 초안으로 시작합니다.",
  saveHintOutstanding:
    "저장하기 전에 남은 검증 문제를 해결해 주세요 — 새 전략은 항상 모의 실행 초안으로 시작합니다.",
  saveError: "전략을 저장하지 못했습니다.",

  jsonEmpty: "비어 있음",
  jsonAddField: "필드 추가",
  jsonFieldName: "필드 이름",
};
